import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css"; // Import Flatpickr CSS
import { useGetTransactionsListingQuery } from "../../../rtk-query/slices/finance/financeSlice";
import { formatTransactionDate } from "../../../helpers/dateFormatter";
import { ShimmerTable } from "react-shimmer-effects";
import NoBookingCard from "../../bookings/components/NoBookingCard";
import { toast } from "react-toastify";

const PaymentDetails = () => {
  const { data: transactionData, isLoading } = useGetTransactionsListingQuery();

  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Date states for start and end
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Refs for Flatpickr instances
  const flatpickrStartRef = useRef(null);
  const flatpickrEndRef = useRef(null);

  // Toggle individual checkbox
  const toggleCheckbox = (id) => {
    setSelectedPayments((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Check if all checkboxes are selected
  const isChecked = (id) => selectedPayments.includes(id);

  // Toggle select all checkboxes
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedPayments([]); // Deselect all
    } else {
      setSelectedPayments(
        transactionData?.data?.payments?.map((item) => item.id)
      ); // Select all
    }
    setSelectAll(!selectAll);
  };

  // Update selectAll state when selectedPayments changes
  useEffect(() => {
    setSelectAll(
      selectedPayments.length === transactionData?.data?.payments?.length
    );
  }, [selectedPayments]);

  // Function to download a single receipt and selected ones
  const downloadReceipts = async (rowPayment) => {
    const paymentsToDownload =
      selectedPayments.length > 0
        ? transactionData?.data?.payments?.filter((payment) =>
            selectedPayments.includes(payment.id)
          )
        : [rowPayment];

    for (const payment of paymentsToDownload) {
      const receiptUrl = payment.stripe_payment_receipt_url;

      if (receiptUrl) {
        try {
          // Fetch the receipt as a Blob
          const pdfLink = document.createElement("a");
          pdfLink.href = receiptUrl; // Set the href to the PDF link
          pdfLink.target = "_blank"; // Open the PDF in a new tab
          pdfLink.download = `receipt_${payment.id}.pdf`; // Set the desired file name for download
          document.body.appendChild(pdfLink);
          pdfLink.click(); // Trigger download
          document.body.removeChild(pdfLink);
          // const response = await fetch(receiptUrl);

          // if (!response.ok) {
          //   throw new Error("Failed to download receipt");
          // }

          // const blob = await response.blob();

          // // Create a URL for the Blob and download the file
          // const url = window.URL.createObjectURL(blob);
          // const link = document.createElement("a");
          // link.href = url;

          // // Set the download attribute with a default name
          // link.setAttribute("download", `receipt-${payment.id}.pdf`);

          // // Append link to the body to trigger the download
          // document.body.appendChild(link);
          // link.click();

          // // Clean up the link and the blob URL
          // document.body.removeChild(link);
          // window.URL.revokeObjectURL(url);
        } catch (error) {
          toast.error("An unexpected error occurred!", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }
    }
  };

  // Function to download all receipts
  const downloadAllReceipts = () => {
    transactionData?.data?.payments?.forEach((payment) => {
      downloadReceipts(payment);
    });
  };

  return (
    <div className="finance-detail-container p-4">
      {/* Payout Details Section */}
      <div className="finance-detail-header mb-4">
        <h5 className="finance-detail-title">Payout Details</h5>
        <p className="finance-detail-info">
          You can withdraw your payment on the 5th of each month
        </p>
        <div className="d-flex align-items-center finance-detail-amount-container">
          <h2 className="finance-detail-amount mb-0">
            AED {transactionData?.data?.totalAmount}
          </h2>
          <button
            className="finance-detail-withdraw-btn"
            disabled={isLoading || transactionData?.data?.totalAmount === 0}
          >
            Withdraw
          </button>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 transaction-date-select-container">
        <div className="finance-detail-date-range d-flex">
          {/* Start Date Picker */}
          <div className="fp-filters-date d-flex align-items-center gap-2">
            <div className="dod-schedule-date-container position-relative">
              <span
                className="dod-schedule-calender-icon me-2"
                onClick={() => flatpickrStartRef.current.flatpickr.open()}
              >
                <Icon icon="uis:calender" fontSize={20} />
              </span>
              <Flatpickr
                className="dod-schedule-flatpickr-container"
                name="startDate"
                placeholder="From"
                value={startDate}
                options={{
                  dateFormat: "l, M j, Y",
                  static: true,
                  disableMobile: true,
                }}
                ref={flatpickrStartRef}
                onChange={([date]) => {
                  const selectedDate = date ? new Date(date) : null;

                  // Convert to UTC (if needed)
                  if (selectedDate) {
                    selectedDate.setMinutes(
                      selectedDate.getMinutes() -
                        selectedDate.getTimezoneOffset()
                    );
                  }

                  setStartDate(selectedDate);
                }}
              />
            </div>
          </div>
          {/* End Date Picker */}
          <div className="fp-filters-date d-flex align-items-center gap-2">
            <div className="dod-schedule-date-container position-relative">
              <span
                className="dod-schedule-calender-icon me-2"
                onClick={() => flatpickrEndRef.current.flatpickr.open()}
              >
                <Icon icon="uis:calender" fontSize={20} />
              </span>
              <Flatpickr
                className="dod-schedule-flatpickr-container"
                name="endDate"
                placeholder="To"
                value={endDate}
                options={{
                  dateFormat: "l, M j, Y",
                  static: true,
                  disableMobile: true,
                }}
                ref={flatpickrEndRef}
                onChange={([date]) => {
                  const selectedDate = date ? new Date(date) : null;

                  // Convert to UTC (if needed)
                  if (selectedDate) {
                    selectedDate.setMinutes(
                      selectedDate.getMinutes() -
                        selectedDate.getTimezoneOffset()
                    );
                  }

                  setEndDate(selectedDate);
                }}
              />
            </div>
          </div>
        </div>
        {selectAll && (
          <button
            className="transaction-all-download-btn"
            onClick={downloadAllReceipts}
          >
            <Icon icon="ph:download-bold" /> Download All
          </button>
        )}
      </div>

      {/* Payout Table */}

      {isLoading ? (
        <ShimmerTable row={5} col={5} />
      ) : (
        <div className="table-responsive-wrapper">
          {transactionData?.data?.payments?.length > 0 ? (
            <table className="w-100 finance-detail-table">
              <thead>
                <tr>
                  <th>
                    {/* Custom Select All Checkbox */}
                    <div
                      className={`custom-checkbox ${
                        selectAll ? "custom-checkbox-checked" : ""
                      }`}
                      onClick={toggleSelectAll}
                      role="checkbox"
                      aria-checked={selectAll}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSelectAll();
                      }}
                    >
                      {selectAll && (
                        <span className="checkmark">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="17"
                            height="18"
                            viewBox="0 0 17 18"
                            fill="none"
                          >
                            <path
                              d="M2.81836 9.5108L6.30075 12.969L14.1361 5.18805"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </div>
                  </th>
                  <th>Date Paid</th>
                  <th>Currency</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {transactionData?.data?.payments?.map((payment) => (
                  <tr key={payment.id}>
                    <td>
                      {/* Individual Custom Checkbox */}
                      <div
                        className={`custom-checkbox ${
                          isChecked(payment.id) ? "custom-checkbox-checked" : ""
                        }`}
                        onClick={() => toggleCheckbox(payment.id)}
                        role="checkbox"
                        aria-checked={isChecked(payment.id)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleCheckbox(payment.id);
                        }}
                      >
                        {isChecked(payment.id) && (
                          <span className="checkmark">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="18"
                              viewBox="0 0 17 18"
                              fill="none"
                            >
                              <path
                                d="M2.81836 9.5108L6.30075 12.969L14.1361 5.18805"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{formatTransactionDate(payment.payment_date)}</td>
                    <td>{payment.currency}</td>
                    <td>{parseFloat(payment.amount)}</td>
                    <td>
                      <button
                        className="finance-detail-download-btn"
                        onClick={() => downloadReceipts(payment)}
                      >
                        <Icon icon="ph:download-bold" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoBookingCard
              text="No transactions found!"
              paddingY={0}
              marginY={0}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentDetails;
